import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
  cancelOwnSubscription,
  type CancelSubscriptionPayload,
} from "@/features/founders/api/founders";
import { useToast } from "@/hooks/use-toast";

const REASONS = [
  { value: "too_expensive", label: "Too expensive" },
  { value: "not_using", label: "Not using it enough" },
  { value: "better_alternative", label: "Found a better alternative" },
  { value: "missing_features", label: "Missing features I need" },
  { value: "other", label: "Other" },
] as const;

const reasonSchema = z
  .object({
    reason: z.enum([
      "too_expensive",
      "not_using",
      "better_alternative",
      "missing_features",
      "other",
    ]),
    otherText: z.string().optional(),
  })
  .refine(
    (data) =>
      data.reason !== "other" ||
      (data.otherText !== undefined && data.otherText.trim().length > 0),
    {
      message: "Please tell us a bit more",
      path: ["otherText"],
    },
  );

type ReasonFormValues = z.infer<typeof reasonSchema>;

type Step = "confirm" | "reason" | "success";

interface CancelSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  endDateLabel: string;
  // Optional override — admin variant can pass a different mutation that
  // calls /licensees/:id/admin-cancel-subscription. Default is the
  // founder-initiated /licensees/cancel-subscription endpoint.
  onSubmitOverride?: (payload: CancelSubscriptionPayload) => Promise<unknown>;
  // Optional optimistic-update hook. The source of truth is the ["me"] query
  // (or the admin founders list when overridden), but parents may still want
  // a callback for non-data side effects (analytics, table refresh, etc.).
  onCancelled?: () => void;
}

const CancelSubscriptionDialog = ({
  open,
  onOpenChange,
  endDateLabel,
  onSubmitOverride,
  onCancelled,
}: CancelSubscriptionDialogProps) => {
  const [step, setStep] = useState<Step>("confirm");
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const form = useForm<ReasonFormValues>({
    resolver: zodResolver(reasonSchema),
    defaultValues: { reason: undefined as unknown as ReasonFormValues["reason"], otherText: "" },
  });

  const selectedReason = form.watch("reason");

  useEffect(() => {
    if (!open) {
      // Reset on close so reopening starts fresh.
      setStep("confirm");
      form.reset({ reason: undefined as unknown as ReasonFormValues["reason"], otherText: "" });
    }
  }, [open, form]);

  const { mutate, isPending } = useMutation({
    mutationFn: async (payload: CancelSubscriptionPayload) => {
      if (onSubmitOverride) {
        return onSubmitOverride(payload);
      }
      return cancelOwnSubscription(payload);
    },
    onSuccess: () => {
      // Refresh CurrentUser so DangerZoneSection re-renders in cancelled state
      // (drives `isCancelled` from subscriptionCancellationRequestedAt).
      queryClient.invalidateQueries({ queryKey: ["me"] });
      setStep("success");
      onCancelled?.();
    },
    onError: (error: unknown) => {
      const message =
        (error as { response?: { data?: { message?: string } }; message?: string })
          ?.response?.data?.message ||
        (error as { message?: string })?.message ||
        "Something went wrong. Please try again.";
      toast({
        variant: "destructive",
        title: "Could not cancel subscription",
        description: message,
      });
    },
  });

  const handleSubmit = form.handleSubmit((values) => {
    mutate({
      reason: values.reason,
      otherText: values.reason === "other" ? values.otherText : undefined,
    });
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        {step === "confirm" && (
          <>
            <DialogHeader>
              <DialogTitle>Are you sure?</DialogTitle>
              <DialogDescription className="text-[#494949]">
                You&apos;ll lose access to Stack — including the content,
                community, Stacey (AI co-pilot), and client opportunities — when
                your existing membership expires at{" "}
                <span className="font-semibold">{endDateLabel}</span>.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col-reverse items-center gap-3 pt-4 sm:flex-col-reverse sm:items-center sm:space-x-0">
              <Button
                type="button"
                variant="outline"
                className="w-full max-w-[320px] leading-10"
                onClick={() => onOpenChange(false)}
              >
                Keep my subscription
              </Button>
              <Button
                type="button"
                variant="default"
                className="w-full max-w-[320px] leading-10"
                onClick={() => setStep("reason")}
              >
                I&apos;m sure, cancel my subscription
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "reason" && (
          <>
            <DialogHeader>
              <DialogTitle>Why are you cancelling?</DialogTitle>
              <DialogDescription>
                Your feedback helps us improve Stack for everyone.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={handleSubmit} className="space-y-4">
                <FormField
                  control={form.control}
                  name="reason"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          className="gap-2"
                        >
                          {REASONS.map((r) => (
                            <div
                              key={r.value}
                              className="flex items-center space-x-2"
                            >
                              <RadioGroupItem
                                value={r.value}
                                id={`cancel-reason-${r.value}`}
                              />
                              <FormLabel
                                htmlFor={`cancel-reason-${r.value}`}
                                className="text-sm font-normal cursor-pointer"
                              >
                                {r.label}
                              </FormLabel>
                            </div>
                          ))}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {selectedReason === "other" && (
                  <FormField
                    control={form.control}
                    name="otherText"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea
                            placeholder="Tell us more..."
                            rows={3}
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                <DialogFooter className="flex-col-reverse items-center gap-3 pt-4 sm:flex-col-reverse sm:items-center sm:space-x-0">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full max-w-[320px] leading-10"
                    onClick={() => setStep("confirm")}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="default"
                    className="w-full max-w-[320px] leading-10"
                    disabled={!selectedReason || isPending}
                  >
                    {isPending ? "Cancelling…" : "Cancel"}
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </>
        )}

        {step === "success" && (
          <>
            <DialogHeader>
              <div className="flex justify-center mb-2">
                <CheckCircle2 className="h-10 w-10 text-[#83b300]" />
              </div>
              <DialogTitle className="text-center">
                Subscription cancelled
              </DialogTitle>
              <DialogDescription className="text-center">
                Your subscription will end at{" "}
                <span className="font-semibold">{endDateLabel}</span>. You will
                not be charged for another year.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col items-center gap-3 pt-4 sm:flex-col sm:items-center sm:space-x-0">
              <Button
                type="button"
                className="w-full max-w-[320px] leading-10"
                onClick={() => onOpenChange(false)}
              >
                Close
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CancelSubscriptionDialog;
